import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  TextField,
  Card,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { getUsers, deleteUser } from "../../State/User/Action";
import { Delete, FileDownload, PictureAsPdf, Print } from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from "dayjs";
import { PermissionService } from "../../../config/permissionService";
import { getSelectedExportRows } from "../../../config/exportSelection";

const Users = () => {
  const dispatch = useDispatch();
  const { adminUser } = useSelector((store) => store);

  const [openDialog, setOpenDialog] = useState(false);
  const [userIdToDelete, setUserIdToDelete] = useState(null);
  const [searchText, setSearchText] = useState("");

  const [selectedRowIds, setSelectedRowIds] = useState([]);


  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  const handleOpenConfirm = (userId) => {
    setUserIdToDelete(userId);
    setOpenDialog(true);
  };

  const handleCloseConfirm = () => {
    setOpenDialog(false);
    setUserIdToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (userIdToDelete) {
      dispatch(deleteUser(userIdToDelete));
    }
    handleCloseConfirm();
  };

  const filteredUsers =
    adminUser.users?.response?.filter(
      (user) =>
        user.role === "CUSTOMER" &&
        (user.name?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.username?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.phone?.toLowerCase().includes(searchText.toLowerCase()))
    ) || [];

  const writeAction = PermissionService.has({ category: "Admin", page: "Customers", type: "WRITE" });

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1,
      renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY HH:mm") : "----",
    },
    { field: "name", headerName: "Name", flex: 1.2, minWidth: 150, renderCell: (params) => (params.value || "----") },
    { field: "username", headerName: "Username", flex: 1.2, minWidth: 150, renderCell: (params) => (params.value || "----") },
    { field: "email", headerName: "Email", flex: 1.5, minWidth: 200, renderCell: (params) => (params.value || "----") },
    { field: "phone", headerName: "Phone", flex: 1.2, minWidth: 150, renderCell: (params) => (params.value || "----") },
    ...(writeAction ? [
      {
        field: "actions",
        headerName: "Actions",
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip title="Delete">
            <IconButton
              color="error"
              size="small"
              sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
              onClick={() => handleOpenConfirm(params.row.id)}
            >
              <Delete fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
            </IconButton>
          </Tooltip>
        ),
      },
    ] : []),
  ];

    const getExportData = () => getSelectedExportRows(filteredUsers, selectedRowIds);

  const handleExportCSV = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const csvContent = exportRows.map((row, index) => [index + 1,
    row.createdAt ? dayjs(row.createdAt).format("DD MMM YYYY HH:mm") : "----", row.name || "----", row.username || "----", row.email || "----", row.phone || "----"
    ].join(',')).join('\n');
    const headers = ['S.No.', 'Created At', 'Name', 'Username', 'Email', 'Phone'].join(',');
    const csv = `${headers}\n${csvContent}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'customers.csv';
    link.click();
  };

  const handleExportPDF = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text("Customer", 14, 15);
    const tableColumn = ['S.No.', 'Created At', 'Name', 'Username', 'Email', 'Phone'];
    const tableRows = exportRows.map((row, index) => [index + 1, row.createdAt ? dayjs(row.createdAt).format("DD MMM YYYY HH:mm") : "----", row.name || "----", row.username || "----", row.email || "----", row.phone || "----"]);
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'striped',
      headStyles: {
        fillColor: [37, 99, 235], // Tailwind blue-600 (RGB: 37,99,235)
        textColor: [255, 255, 255], // white text
        halign: 'left',
      },
      styles: { fontSize: 8, cellPadding: 1, overflow: 'linebreak' },
    });
    doc.save('customers.pdf');
  };

  const handlePrint = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const printWindow = window.open('', '_blank');
    const headers = ['S.No.', 'Created At', 'Name', 'Username', 'Email', 'Phone'];
    const tableContent = exportRows.map((row, index) => [index + 1, row.createdAt ? dayjs(row.createdAt).format("DD MMM YYYY HH:mm") : "----", row.name || "----", row.username || "----", row.email || "----", row.phone || "----"]);
    const html = `
      <html>
        <head>
          <title>Customers</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background: #449AEF;
                color: white;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact; }
            tr:nth-child(even) { background-color: #f5f5f5; }
          </style>
        </head>
        <body>
          <h2>Customers</h2>
          <table>
            <thead>
              <tr>
                ${headers.map(header => `<th>${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${tableContent.map(row => `
                <tr>
                  ${row.map(cell => `<td>${cell}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div >
      <Card className="p-4" sx={{ borderRadius: 2, boxShadow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="h6" marginBottom={0}>
            CUSTOMERS
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              onClick={handleExportCSV}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdf />}
              onClick={handleExportPDF}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Export PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Print
            </Button>
            <TextField
              label="Search Customers"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              sx={{ width: { xs: '100%', sm: '220px' } }}
            />
          </Box>
        </Box>
        <Box sx={{ height: "100%", width: "100%" }}>
          <DataGrid
            rows={filteredUsers}
            columns={columns}
            pagination
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
            pageSizeOptions={[10, 25, 50, 75, 100]}
            getRowId={row => row.id}
            checkboxSelection
            onRowSelectionModelChange={(ids) => setSelectedRowIds(ids)}
            rowSelectionModel={selectedRowIds}
            disableSelectionOnClick
            density="compact"
            experimentalFeatures={{ virtualization: true }}
            loading={adminUser.loading}
            autoHeight={true}
            sx={{
              borderRadius: 2,
              fontSize: "0.8rem",
              "& .MuiDataGrid-cell": {
                fontSize: "0.8rem",
                border: "1px solid rgb(204, 201, 201)",
                minHeight: "36px !important",
                height: "36px !important",
                lineHeight: "1.2",
                paddingTop: "4px",
                paddingBottom: "4px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#449AEF",
                color: "#fff",
                fontWeight: "bold",
                fontSize: "0.8rem",
                padding: "8px",
                border: "1px solid rgb(204, 201, 201)",
                minHeight: "36px !important",
                height: "36px !important",
                lineHeight: "1.2",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "0.8rem",
                fontWeight: 600,
              },
              "& .MuiDataGrid-row": {
                maxHeight: "36px !important",
                minHeight: "36px !important",
              },
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                backgroundColor: "#f5f5f5",
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: "#ffffff",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        </Box>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseConfirm}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Users;

